import type { StorefrontDataSource } from '@/features/storefront/data/source';

/**
 * Which product slugs a shop actually sells, for the sections that name a
 * product by slug - a lookbook hotspot, a showcase, a spotlight.
 *
 * Those slugs come from the theme document, and a theme applied from the
 * platform catalog carries its designer's sample products (LittleJoy's
 * `rainbow-ring-stacker`, `monster-truck-push-along`, ...). Asking the shop for
 * each of them by name is a 404 in the shopper's console on every visit, so a
 * live shop first reads one page of its own catalog and only asks for a slug
 * that may be in it. A catalog larger than that page cannot rule a slug out,
 * so there the product is asked for as before.
 */
export const SLUG_INDEX_SIZE = 100;

export interface ProductSlugIndex {
  slugs: ReadonlySet<string>;
  /** True when the page held the whole catalog, so a slug not in it is not sold. */
  complete: boolean;
}

export async function fetchProductSlugIndex(
  source: Pick<StorefrontDataSource, 'products'>,
): Promise<ProductSlugIndex> {
  const page = await source.products({ size: SLUG_INDEX_SIZE });
  return {
    slugs: new Set(page.items.map((item) => item.slug)),
    complete: !page.meta.has_next,
  };
}

/** Whether `slug` may be one of the shop's products - false only when it is certainly not. */
export function mayBeSold(index: ProductSlugIndex, slug: string): boolean {
  return index.slugs.has(slug) || !index.complete;
}

/** A section's product the shop does not sell; the section draws without it. */
export class UnsoldProductError extends Error {
  constructor(readonly slug: string) {
    super(`No product "${slug}" in this shop.`);
    this.name = 'UnsoldProductError';
  }
}
